import { ResponseMessage } from "../../Models/AccountModel";
import { Alternative, AlternativeFilters, AlternativeNameResponse, newAlternative } from "../../Models/AlternativeModel";
import { RequestAlternativeInformation } from "../../Models/RequestAlternativeInformation";
import { RequestAlternativeSample } from "../../Models/RequestAlternativeSample";
import { SINList } from "../../Models/SINList";
import { MarketplaceGroup } from "../../Models/MarketplaceGroupModel";

export const fetchFilters = async (): Promise<AlternativeFilters> => {
  const data = sessionStorage.getItem("filters");
  if (data) {
    return JSON.parse(data) as AlternativeFilters;
  }
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}alternative/getfilters`);

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: AlternativeFilters = await response.json();
    sessionStorage.setItem("filters", JSON.stringify(data));
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      alternativeLevels: [],
      alternativeToCategories: [],
      certificationsCategories: [],
      sectorCategories: [],
      useCategories: [],
    }; // Return an empty object with the required structure in case of error
  }
};

export const getAlternativeByUrl = async (url: string, isPublished: boolean): Promise<Alternative> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;
  try {
    const response = await fetch(
      `${apiUrl}alternative/getalternativebyurl?url=${url}&isPublished=${isPublished}`
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data: Alternative = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      id: 0,
      title: "",
      isBioBased: false,
      alternativeToCategories: [],
      sectorCategories: [],
      useCategories: [],
      certificationCategories: [],
      companyWebsite: "",
      isPublished: false,
      isPopular: false
    };
  }
};

export const getAlternativeNameByUrl = async (url: string): Promise<AlternativeNameResponse> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;
  try {
    const response = await fetch(`${apiUrl}alternative/GetAlternativeInfo?url=${url}`);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data: AlternativeNameResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {name: "", url: "", contactEmail: ""};
  }
};

export const updateAlternative = async (alternative: newAlternative): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;
  const formData = new FormData();
  formData.append("id", alternative.id.toString());
  formData.append("title", alternative.title);
  formData.append("description", alternative.description);
  formData.append("supplementaryInformation", alternative.supplementaryInformation ?? "");
  formData.append("casNumber", alternative.casNumber ?? "");
  formData.append("isBioBased", alternative.isBioBased.toString());
  formData.append("additionalInformation", alternative.additionalInformation ?? "");
  alternative.companyName ?  formData.append("companyName", alternative.companyName) : "";
  alternative.companyGroupId ? formData.append("companyGroupId", alternative.companyGroupId.toString()) : "";
  formData.append("companyWebsite", alternative.companyWebsite.toString());
  formData.append("contactName", alternative.contactName);
  formData.append("contactEmail", alternative.contactEmail);
  alternative.alternativeToCategories.map((category) => formData.append(`alternativeToCategories`, category.id.toString()));
  alternative.sectorCategories.map((category) => formData.append(`sectorCategories`, category.id.toString()));
  alternative.useCategories.map((category) => formData.append(`useCategories`, category.id.toString()));
  alternative.certificationCategories.map((category) => formData.append(`certificationCategories`, category.id.toString()));
  alternative.technicalDataSheet != undefined && alternative.technicalDataSheet.fileData != null ? formData.append("technicalDataSheet", alternative.technicalDataSheet.fileData) : "";
  alternative.safetyDataSheet && alternative.safetyDataSheet.fileData != null ? formData.append("safetyDataSheet", alternative.safetyDataSheet.fileData) : "";
  alternative.additionalDocuments?.map((doc) =>
    doc.fileData != null
      ? formData.append(`additionalDocuments`, doc.fileData)
      : ""
  );
  alternative.logotype.fileData.name != "" ? formData.append("logotype", alternative.logotype.fileData) : "";
  formData.append("alternativeLevel", alternative.alternativeLevel.toString());

  const existingLogotypeId = alternative.logotype.id == 0 ? 0 : alternative.logotype.id;
  formData.append("existingLogotype", existingLogotypeId.toString());

  const existingTechnicalDataSheetId = alternative.technicalDataSheet != undefined && alternative.technicalDataSheet.id != 0 ? alternative.technicalDataSheet.id : 0;
  formData.append("existingTechnicalDataSheet", existingTechnicalDataSheetId.toString());

  const existingSafetyDataSheetId = alternative.safetyDataSheet != undefined && alternative.safetyDataSheet.id != 0 ? alternative.safetyDataSheet.id : 0;
  formData.append("existingSafetyDataSheet", existingSafetyDataSheetId.toString());

  alternative.additionalDocuments?.map((doc) => {
    const existingAdditionalDocumentId = doc.id == 0 ? 0 : doc.id;
    formData.append(`existingAdditionalDocuments`, existingAdditionalDocumentId.toString());
  });

  try {
    const response = await fetch(`${apiUrl}alternative/Update`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json() as ResponseMessage;
    if (!data.success) {
      throw new Error(data.message);
    }
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error could not updated alternative"
    };
  }

};

export const AddAlternative = async (newAlternative: newAlternative): Promise<any> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;
  const formData = new FormData();
  formData.append("title", newAlternative.title);
  formData.append("description", newAlternative.description);
  formData.append("supplementaryInformation", newAlternative.supplementaryInformation);
  formData.append("casNumber", newAlternative.casNumber);
  formData.append("isBioBased", newAlternative.isBioBased.toString());
  formData.append("additionalInformation", newAlternative.additionalInformation);
  newAlternative.companyName ?  formData.append("companyName", newAlternative.companyName) : "";
  newAlternative.companyGroupId ? formData.append("companyGroupId", newAlternative.companyGroupId.toString()) : "";
  formData.append("companyWebsite", newAlternative.companyWebsite.toString());
  formData.append("contactName", newAlternative.contactName);
  formData.append("contactEmail", newAlternative.contactEmail);
  newAlternative.alternativeToCategories.map((category) => formData.append(`alternativeToCategories`, category.id.toString()));
  newAlternative.sectorCategories.map((category) => formData.append(`sectorCategories`, category.id.toString()));
  newAlternative.useCategories.map((category) => formData.append(`useCategories`, category.id.toString()));
  newAlternative.certificationCategories.map((category) => formData.append(`certificationCategories`, category.id.toString()));
  newAlternative.technicalDataSheet ? formData.append("technicalDataSheet", newAlternative.technicalDataSheet?.fileData) : "";
  newAlternative.safetyDataSheet ? formData.append("safetyDataSheet", newAlternative.safetyDataSheet.fileData) : "";
  newAlternative.additionalDocuments?.map((doc) => formData.append(`additionalDocuments`, doc.fileData));
  formData.append("logotype", newAlternative.logotype.fileData);
  formData.append("alternativeLevel", newAlternative.alternativeLevel.toString());

  try {
    const response = await fetch(`${apiUrl}alternative/AddAlternative`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {}; // Return an empty object with the required structure in case of error
  }
};

export const publishAlternative = async (id: number): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}Admin/PublishAlternative`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(id),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Error could not publish alternative",
    };
  }
};

export const deleteAlternative = async (id: number): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}Alternative/Delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(id),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error could not delete alternative",
    };
  }
};

export const updateAlternativeLevel = async (id: number, level: number): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}Admin/AlternativeLevel?id=${id}&level=${level}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error could not update alternativeLevel",
    };
  }
};

export const updateIsPopular = async (id: number, popular: boolean): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}Admin/Popular?id=${id}&isPopular=${popular}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error could not update isPopular",
    };
  }
};

export const RequestMoreInformation = async (data: RequestAlternativeInformation): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  const formData = new FormData();
  formData.append("url", data.url);
  formData.append("email", data.email);
  formData.append("message", data.message);
  formData.append("alternativeContactEmail", data.alternativeContactEmail);
  formData.append("alternativeName", data.alternativeName);
  // Include reCAPTCHA token if available
  if (data.recaptchaToken) {
    formData.append("recaptchaToken", data.recaptchaToken);
  }
  try {
    const response = await fetch(`${apiUrl}Alternative/MoreInformation`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error something went wrong with requesting more information",
    };
  }
};

export const RequestSampleOrQuote = async (data: RequestAlternativeSample): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;
  const formData = new FormData();
  formData.append("url", data.url);
  formData.append("fullName", data.fullName)
  formData.append("email", data.email);
  formData.append("phone", data.phone);
  formData.append("deliveryAddress", data.deliveryAddress);
  formData.append("city", data.city);
  formData.append("region", data.region);
  formData.append("country", data.country);
  formData.append("zipcode", data.zipCode);
  formData.append("samples", data.samples.toString());
  formData.append("volume", data.volume.toString());
  // Include reCAPTCHA token if available
  if (data.recaptchaToken) {
    formData.append("recaptchaToken", data.recaptchaToken);
  }
  formData.append("volumeUnit", data.volumeUnit);
  formData.append("message", data.message);
  formData.append("alternativeContactEmail", data.alternativeContactEmail);
  formData.append("alternativeName", data.alternativeName);
  try {
    const response = await fetch(`${apiUrl}Alternative/SampleOrQuote`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error something went wrong with requesting a sample or quote",
    };
  }
};

export const UpdateOwner = async (adId: number, owner: string): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}Admin/ChangeOwner?id=${adId}&userName=${owner}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error could not update owner on alternative",
    };
  }
};

export const GetSelectedChemicals = async (id: number): Promise<SINList[]> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}Admin/GetSelectedChemicals?id=${id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: SINList[] = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return [{
      chemicalId: 0,
      name: "",
      cas: "",
      selected: false
    }];
  }
}

export const SearchSinList = async (id: number, search: string): Promise<SINList[]> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}Admin/SearchSinList?id=${id}&term=${search}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: SINList[] = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return [];
  }
}

export const SaveSinListItems = async (id: number, sinListItems: SINList[]): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}Admin/SelectChemicals?id=${id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(sinListItems)
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error could not save SIN list items"
    };
  }
}

export const GetSelectedMarketplaceGroups = async (id: number): Promise<MarketplaceGroup[]> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}admin/GetSelectedMarketplaceGroups?id=${id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: MarketplaceGroup[] = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return [];
  }
}

export const SelectMarketplaceGroups = async (id: number, selectedMarketplaceGroups: MarketplaceGroup[]): Promise<ResponseMessage> => {
  const apiUrl = import.meta.env.VITE_BACKEND_HOST_NAME;

  try {
    const response = await fetch(`${apiUrl}admin/SelectMarketplaceGroups?id=${id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(selectedMarketplaceGroups)
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ResponseMessage = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    return {
      success: false,
      message: "Error could not save marketplace groups"
    };
  }
}